import { useAccountReps } from "frontend/hooks/use-account-reps";
import { useSetAtom } from "jotai";
import { createContext, MutableRefObject, useContext, useRef } from "react";
import { undoRedoStackAtom } from "state-atoms";
import { useUndoRedo, type UseUndoRedoResult } from "utils/undo-redo/use-undo-redo";
import UndoRedoStack from "utils/undo-redo/undo-redo-stack";
import { Reflect } from "@workcanvas/reflect/client";
import { M } from "shared/datamodel/mutators";

export const UndoRedoContext = createContext<
  Partial<{
    undoRedoStack?: UndoRedoStack;
    setUndoRedoStack: (undoRedoStack: UseUndoRedoResult["stack"]) => void;

    executeMutation: UseUndoRedoResult["addAction"];
    patchElementsWithUndoRedo: UseUndoRedoResult["patchElements"];
  }> & {
    canUndo: boolean;
    canRedo: boolean;
    executeUndo: UseUndoRedoResult["undo"];
    executeRedo: UseUndoRedoResult["redo"];
    needToRefreshBoundsRef: MutableRefObject<string | null>;
  }
>({
  canUndo: false,
  canRedo: false,
  executeUndo: () => {},
  executeRedo: () => {},
  needToRefreshBoundsRef: { current: null },
});

export const useUndoRedoControls = () => {
  return useContext(UndoRedoContext);
};

type UndoRedoProviderProps = {
  children: React.ReactNode;
  documentId?: string;
  reps: Reflect<M>;
};

export const UndoRedoProvider = ({ children, documentId, reps }: UndoRedoProviderProps) => {
  const needToRefreshBoundsRef = useRef<string | null>(null);

  const setUndoRedoStack = useSetAtom(undoRedoStackAtom);
  const { isAccountRepsReady, updateBoardLastChanged } = useAccountReps();

  const {
    stack: undoRedoStack,
    state: { canUndo, canRedo },
    addAction: executeMutation,
    undo: executeUndo,
    redo: executeRedo,
    patchElements: patchElementsWithUndoRedo,
  } = useUndoRedo(
    reps,
    () => {
      // update board changed for every mutation. TODO: This is creating a severe degregation in performance, should think of a more preformant way to achieve this
      if (isAccountRepsReady && documentId) {
        (async function () {
          await updateBoardLastChanged(documentId);
        })();
      }
    },
    needToRefreshBoundsRef
  );
  setUndoRedoStack(undoRedoStack);

  return (
    <UndoRedoContext.Provider
      value={{
        undoRedoStack,
        setUndoRedoStack,
        canUndo,
        canRedo,
        executeMutation,
        executeUndo,
        executeRedo,
        patchElementsWithUndoRedo,
        needToRefreshBoundsRef,
      }}
    >
      {children}
    </UndoRedoContext.Provider>
  );
};
